import React from 'react';
import { Box, Container, Grid, Typography, Button, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ServiceCard from '../components/ServiceCard';
import SectionHeading from '../components/SectionHeading';
import { gradients } from '../theme/theme';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const services = [
  { key: 'telecom', icon: PhoneInTalkIcon, image: '/services/telecom.jpg' },
  { key: 'logistics', icon: LocalShippingIcon, image: '/services/logistics.jpg' },
  { key: 'consulting', icon: BusinessCenterIcon, image: '/services/consulting.jpg' },
  { key: 'tourism', icon: FlightTakeoffIcon, image: '/services/tourism.jpg' },
  { key: 'industrial', icon: PrecisionManufacturingIcon, image: '/services/industrial.jpg' },
  { key: 'internet', icon: LanguageIcon, image: '/services/internet.jpg' },
];

const steps = ['s1', 's2', 's3', 's4'];

const ServicesPage = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Services grid */}
      <Box sx={{ pt: { xs: 12, md: 16 }, pb: { xs: 7, md: 11 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={t('services.eyebrow')}
            title={t('services.title')}
            subtitle={t('services.subtitle')}
            sx={{ mb: 8 }}
          />

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={service.key}>
                <ServiceCard
                  title={t(`services.items.${service.key}.title`)}
                  description={t(`services.items.${service.key}.description`)}
                  icon={service.icon}
                  image={service.image}
                  index={index}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process — how we work */}
      <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={t('services.process.eyebrow')}
            title={t('services.process.title')}
            subtitle={t('services.process.subtitle')}
            sx={{ mb: 7 }}
          />

          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={step}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.12 }}
                  viewport={{ once: true }}
                  style={{ height: '100%' }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      height: '100%',
                      p: 3.5,
                      borderRadius: 4,
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'all 0.35s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 24px 50px -28px rgba(13,71,161,0.4)',
                        borderColor: 'rgba(21,101,192,0.35)',
                      },
                    }}
                  >
                    {/* step number */}
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        background: gradients.primary,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                        fontWeight: 800,
                        fontSize: '1.15rem',
                        mb: 2.5,
                        boxShadow: '0 14px 28px -12px rgba(13,71,161,0.8)',
                      }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                      {t(`services.process.steps.${step}.title`)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      {t(`services.process.steps.${step}.desc`)}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA band */}
      <Box sx={{ py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 6,
                px: { xs: 3, md: 8 },
                py: { xs: 5, md: 7 },
                background: gradients.primary,
                boxShadow: '0 30px 60px -30px rgba(13,71,161,0.6)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.4,
                  background: 'radial-gradient(circle at 85% 15%, rgba(255,122,69,0.35), transparent 40%)',
                }}
              />
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={4}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                sx={{ position: 'relative' }}
              >
                <Box sx={{ maxWidth: 640 }}>
                  <Typography variant="h3" sx={{ color: 'white', mb: 1.5 }}>
                    {t('services.cta.title')}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: '1.05rem', lineHeight: 1.7 }}>
                    {t('services.cta.desc')}
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to="/iletisim"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    flexShrink: 0,
                    bgcolor: 'white',
                    color: 'primary.dark',
                    px: 4,
                    py: 1.6,
                    fontSize: '1rem',
                    fontWeight: 700,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {t('services.cta.button')}
                </Button>
              </Stack>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default ServicesPage;
